import {
    InfiniteData,
    useInfiniteQuery,
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { TravelerForm } from '../traveler/traveler.types';
import { useSuperUser } from '../../api.context';
import {
    BookingNote,
    BookingProductOption,
    BookingStatus,
    UseBookingsLazyData,
} from './booking.types';
import {
    BookingsService,
    EventsService,
    ListItemAction,
    type GetBookingsResItem0Dto,
    type GetBookingsResItem1Dto,
    type UpdateEventBookingsResDto,
    type UpdateEventBookingsRes1Dto,
    type GetBookingResDto,
    type GetBookingRes1Dto,
    type GetBookingsResDto,
    AttendanceType,
    GetEventsResDto,
    GetEventsResItem0Dto,
    GetEventsResItem1Dto,
    GetEventResDto,
    GetEventRes1Dto,
    TimezoneName,
    DiscountData,
    GetOrderResDto,
    DiscountType,
    type NoteVisibility,
    BookingSourceType,
    ExportReportsService,
    type ExportBookingsExportResDto,
    type ExportBookingsExportRes1Dto,
    type ExportBookingsExportRes2Dto,
    CreateBookingResDto,
    CreateBookingRes1Dto,
} from '../../generated';
import {
    convertBookingOptionToBookingOptionDto,
    convertBookingTravelerToBookingTravelerDto,
    getBookingDtoToBooking,
} from './booking.converters';
import { useOrderKey } from '../order/order.hooks';
import { useEventKey, useEventsLazyKey } from '../event/event.hooks';
import { EventItemMinimal } from '../event/event.types';
import { Customer } from '../customer/customer.types';
import { Referral } from '../team/team.types';
import {
    CustomInfiniteQueryOptions,
    CustomMutationOptions,
    CustomQueryOptions,
    SourceType,
    PaxData,
    TransactionType,
    DirectSource,
    TransactionMethod,
} from '../common.types';
import {
    convertDateToTimestamp,
    convertItemsToActionItemsDto,
} from '../common.converters';

const mergeItems = (
    item: GetBookingsResItem0Dto | GetBookingsResItem1Dto,
    newItem: UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
): GetBookingsResItem0Dto | GetBookingsResItem1Dto =>
    ({
        ...item,
        ...newItem,
    } as GetBookingsResItem0Dto | GetBookingsResItem1Dto);

const updateBooking = (
    item: EventDto,
    newItem: UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    // @ts-ignore-next-line
): EventDto => ({
    ...item,
    bookings: item.bookings.map(b =>
        b.id === newItem.id ? mergeItems(b, newItem) : b
    ),
});

const updateOrderBooking = (
    item: GetOrderResDto,
    newItem: UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    // @ts-ignore-next-line
): GetOrderResDto => ({
    ...item,
    // @ts-ignore-next-line
    bookings: item.bookings?.map(b =>
        b.id === newItem.id ? mergeItems(b, newItem) : b
    ),
});

export const useBookingsLazyKey = 'useBookingsLazyKey';
const useBookingKey = 'useBookingKey';

type EventDto =
    | GetEventsResItem0Dto
    | GetEventsResItem1Dto
    | GetEventResDto
    | GetEventRes1Dto;

export const useBookingCache = () => {
    const queryClient = useQueryClient();

    const update = (
        id: string,
        newItem: UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    ) => {
        queryClient
            .getQueriesData({
                queryKey: [useBookingsLazyKey],
                exact: false,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<InfiniteData<GetBookingsResDto>>(
                    queryKey,
                    data => {
                        if (!data?.pages) return undefined;
                        const pages = data.pages.map(page => {
                            const items =
                                page.items?.map(item =>
                                    item.id === id
                                        ? mergeItems(item, newItem)
                                        : item
                                ) || [];
                            return { ...page, items };
                        });
                        return { ...data, pages };
                    }
                );
            });
        queryClient
            .getQueriesData({
                queryKey: [useBookingKey, id],
                exact: true,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<GetBookingResDto | GetBookingRes1Dto>(
                    queryKey,
                    (data?: GetBookingResDto | GetBookingRes1Dto) => {
                        return data
                            ? (mergeItems(data, newItem) as GetBookingResDto)
                            : undefined;
                    }
                );
            });

        // Bookings under event
        if (newItem.event?.id) {
            queryClient
                .getQueriesData({
                    queryKey: [useEventsLazyKey],
                    exact: false,
                })
                .forEach(([queryKey]) => {
                    queryClient.setQueryData<InfiniteData<GetEventsResDto>>(
                        queryKey,
                        data => {
                            if (!data?.pages) return undefined;
                            const pages = data.pages.map(page => {
                                const items =
                                    page.items?.map(item =>
                                        item.id === newItem.event?.id
                                            ? (updateBooking(item, newItem) as
                                                  | GetEventsResItem0Dto
                                                  | GetEventsResItem1Dto)
                                            : item
                                    ) || [];
                                return { ...page, items };
                            });
                            return { ...data, pages };
                        }
                    );
                });
            queryClient
                .getQueriesData({
                    queryKey: [useEventKey],
                    exact: false,
                })
                .forEach(([queryKey]) => {
                    queryClient.setQueryData<EventDto>(queryKey, data => {
                        return data
                            ? (updateBooking(data, newItem) as
                                  | GetEventResDto
                                  | GetEventRes1Dto)
                            : undefined;
                    });
                });
        }

        // Bookings under order
        if (newItem.order?.id) {
            queryClient
                .getQueriesData({
                    queryKey: [useOrderKey, newItem.order.id],
                    exact: true,
                })
                .forEach(([queryKey]) => {
                    queryClient.setQueryData<GetOrderResDto>(queryKey, data => {
                        return data
                            ? (updateOrderBooking(
                                  data,
                                  newItem
                              ) as GetOrderResDto)
                            : undefined;
                    });
                });
        }
    };

    return {
        update,
    };
};

export const useBooking = (
    bookingId?: string,
    options: CustomQueryOptions<GetBookingResDto | GetBookingRes1Dto> = {}
) => {
    const queryKey = [useBookingKey, bookingId];

    const { data, ...other } = useQuery({
        queryKey,
        queryFn: async () => {
            const response = await BookingsService.getBooking({
                path: { booking_id: bookingId as string },
            });
            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        enabled: !!bookingId,
        ...options,
    });

    const parsedData = useMemo(
        () => (data ? getBookingDtoToBooking(data) : data),
        [data]
    );

    return {
        data: parsedData,
        ...other,
    };
};

export const useBookingsLazy = (
    params: UseBookingsLazyData = {},
    options: CustomInfiniteQueryOptions<GetBookingsResDto> & {
        onInvalidPagination?: () => void;
    } = {}
) => {
    const pageSize = params.pageSize || 5;

    const { data, ...other } = useInfiniteQuery({
        queryKey: [useBookingsLazyKey, params],
        queryFn: async ({ pageParam }) => {
            const response = await BookingsService.getBookings({
                query: {
                    action: params.action,
                    at_start: params.at_start,
                    at_end: params.at_end,
                    by_name: params.by,
                    product_types: params.types,
                    statuses: params.statuses,
                    order_number: params.order_number,
                    start_ts: params.startTs,
                    end_ts: params.endTs,
                    source_type:
                        params.sourceType as unknown as BookingSourceType,
                    source_name: params.sourceName,
                    source_email: params.sourceEmail,
                    text_search: params.text_search,
                    page_number: pageParam?.page || 0,
                    page_size: pageSize,
                    start_at_id: pageParam?.startAtId,
                },
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options.onInvalidPagination?.();
            } else options.onError?.(error);
        },
    });

    // TODO move this to common converters
    const totalCount = data?.pages.find(page => page.total_count)?.total_count;
    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(page =>
                          (page.items || []).map(getBookingDtoToBooking)
                      )
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        totalCount,
        data: parsedData,
        ...other,
    };
};

export const useHoldBookingAvailability = (
    options?: CustomMutationOptions<
        { bookingId: string },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({ bookingId }) => {
            const response = await BookingsService.updateStatusBookings({
                path: { booking_id: bookingId },
                body: {
                    status: BookingStatus.HOLD,
                },
                query: superUser ? { superuser: superUser } : {},
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useReserveBooking = (
    options?: CustomMutationOptions<
        { bookingId: string },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({ bookingId }) => {
            const response = await BookingsService.updateStatusBookings({
                path: { booking_id: bookingId },
                body: {
                    status: BookingStatus.CONFIRMED,
                },
                query: superUser ? { superuser: superUser } : {},
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useCancelBooking = (
    options?: CustomMutationOptions<
        { bookingId: string; reason?: string },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({ bookingId, reason }) => {
            const response = await BookingsService.updateStatusBookings({
                path: { booking_id: bookingId },
                body: {
                    status: BookingStatus.CANCELLED,
                    message: reason,
                },
                query: superUser ? { superuser: superUser } : {},
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateBookingSource = (
    options?: CustomMutationOptions<
        {
            bookingId: string;
            sourceType: SourceType;
            sourceName?: string;
            memberId?: string;
        },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const queryClient = useQueryClient();
    const { update } = useBookingCache();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({
            bookingId,
            sourceName: name,
            sourceType: type,
            memberId,
        }) => {
            const response = await BookingsService.updateBookingSourceBookings({
                path: { booking_id: bookingId },
                body:
                    type === SourceType.DIRECT
                        ? {
                              type,
                              name: name as DirectSource,
                          }
                        : {
                              type,
                              member_id: memberId as string,
                          },
                query: superUser ? { superuser: superUser } : {},
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            if (args[0].order.id) {
                queryClient.invalidateQueries({
                    queryKey: [useOrderKey, args[0].order.id],
                    exact: true,
                    refetchType: 'active',
                });
            }
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateBookingDiscount = (
    options?: CustomMutationOptions<
        { bookingId: string; amount?: number; type: DiscountType },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const queryClient = useQueryClient();
    const { update } = useBookingCache();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({ bookingId, amount, type }) => {
            const response = await BookingsService.updateDiscountBookings({
                path: { booking_id: bookingId },
                body: {
                    type,
                    amount: amount || 0,
                },
                query: superUser ? { superuser: superUser } : {},
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            if (args[0].order.id) {
                queryClient.invalidateQueries({
                    queryKey: [useOrderKey, args[0].order.id],
                    exact: true,
                    refetchType: 'active',
                });
            }
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateBookingParticipants = (
    oldItems: TravelerForm[],
    options?: CustomMutationOptions<
        {
            bookingId: string;
            items: TravelerForm[];
            pax: PaxData;
        },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async ({ bookingId, items, pax }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(convertBookingTravelerToBookingTravelerDto),
                items.map(convertBookingTravelerToBookingTravelerDto),
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );
            const response = await BookingsService.updateCustomersBookings({
                path: { booking_id: bookingId },
                body: {
                    pax,
                    items: requestItems,
                },
                query: superUser ? { superuser: superUser } : {},
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
        onError: (error, ...args) => {
            if (
                // @ts-ignore
                error?.status === 409 &&
                // @ts-ignore
                error?.body?.message.indexOf('Nothing') > -1
            ) {
                options?.onSuccess?.({} as UpdateEventBookingsResDto, ...args);
            } else {
                options?.onError?.(error, ...args);
            }
        },
    });
};

export const useAddBookingTransaction = (
    options?: CustomMutationOptions<
        {
            bookingId: string;
            type: TransactionType;
            method: TransactionMethod;
            amount: number;
        },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();

    return useMutation({
        mutationFn: async ({ bookingId, type, method, amount }) => {
            const response = await BookingsService.updateTransactionsBookings({
                path: { booking_id: bookingId },
                body: {
                    action: ListItemAction.ADD,
                    type,
                    method,
                    amount: {
                        original: amount,
                    },
                },
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useDiscardBookingTransaction = (
    options?: CustomMutationOptions<
        {
            bookingId: string;
            transactionId: string;
            message?: string;
        },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();

    return useMutation({
        mutationFn: async ({ bookingId, transactionId, message }) => {
            const response = await BookingsService.updateTransactionsBookings({
                path: { booking_id: bookingId },
                body: {
                    action: ListItemAction.UPDATE,
                    id: transactionId,
                    discard: { message: message || '' },
                },
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useExportBookings = (
    filters: UseBookingsLazyData,
    options?: CustomMutationOptions<
        {
            name: string;
        },
        | ExportBookingsExportResDto
        | ExportBookingsExportRes1Dto
        | ExportBookingsExportRes2Dto
    >
) => {
    return useMutation({
        mutationFn: async ({ name }) => {
            const response = await ExportReportsService.exportBookingsExport({
                query: {
                    filename: name,
                    action: filters.action,
                    at_start: filters.at_start,
                    at_end: filters.at_end,
                    by_name: filters.by,
                    product_types: filters.types,
                    statuses: filters.statuses,
                    order_number: filters.order_number,
                    start_ts: filters.startTs,
                    end_ts: filters.endTs,
                    source_type:
                        filters.sourceType as unknown as BookingSourceType,
                    source_name: filters.sourceName,
                    source_email: filters.sourceEmail,
                    text_search: filters.text_search,
                },
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
    });
};

export const useUpdateBookingOptions = (
    oldItems: BookingProductOption[],
    options: CustomMutationOptions<
        {
            bookingId: string;
            items: (BookingProductOption & { sync_pricing: boolean })[];
        },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto | null
    > = {}
) => {
    const queryClient = useQueryClient();
    const { update } = useBookingCache();

    return useMutation({
        mutationFn: async ({ bookingId, items }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(convertBookingOptionToBookingOptionDto),
                items.map(convertBookingOptionToBookingOptionDto),
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );

            if (requestItems.length) {
                const response =
                    await BookingsService.updateProductOptionsBookings({
                        path: { booking_id: bookingId },
                        body: {
                            items: requestItems,
                        },
                    });

                return response.data || null;
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].bookingId, args[0]);
            if (args[0]?.order.id) {
                queryClient.invalidateQueries({
                    queryKey: [useOrderKey, args[0].order.id],
                    exact: true,
                    refetchType: 'active',
                });
            }
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateBookingNotes = (
    oldItems: BookingNote[],
    options?: CustomMutationOptions<
        { bookingId: string; notes: BookingNote[] },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto | null
    >
) => {
    const { update } = useBookingCache();

    return useMutation({
        mutationFn: async ({ bookingId, notes }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems,
                notes,
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );

            if (requestItems.length) {
                const response =
                    await BookingsService.updateBookingNotesBookings({
                        path: { booking_id: bookingId },
                        body: {
                            items: requestItems,
                        },
                    });

                return response.data || null;
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useMoveBooking = (
    options?: Omit<
        UseMutationOptions<
            UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto,
            unknown,
            {
                bookingId: string;
                bookingStatus: BookingStatus;
                event: EventItemMinimal;
                syncPricing: boolean;
            },
            unknown
        >,
        'mutationFn'
    >
) => {
    const superUser = useSuperUser();
    return useMutation(
        async ({ bookingId, bookingStatus, event, syncPricing }) => {
            let eventId = event.id;
            const isDraft = bookingStatus === BookingStatus.DRAFT;
            if (!eventId && isDraft) {
                const response = await BookingsService.updateEventBookings({
                    path: { booking_id: bookingId },
                    body: {
                        capacity_option_id: event.capacityId,
                        date: {
                            start: convertDateToTimestamp(
                                new Date(event.start),
                                true
                            ),
                            timezone: {
                                name: Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone as TimezoneName,
                            },
                        },
                    },
                    query: {
                        sync_pricing: syncPricing,
                        ...(superUser ? { superuser: superUser } : {}),
                    },
                });

                if (!response.data) throw new Error('Error');
                return response.data;
            }

            if (!eventId) {
                const newEvent = await EventsService.createEvent({
                    body: {
                        date: {
                            start: convertDateToTimestamp(
                                new Date(event.start),
                                true
                            ),
                            timezone: {
                                name: Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone as TimezoneName,
                            },
                        },
                        product: {
                            id: event.productId,
                            capacity_id: event.capacityId,
                        },
                    },
                });
                eventId = newEvent.data?.id;
            }

            const response = await BookingsService.updateEventBookings({
                path: { booking_id: bookingId },
                body: {
                    id: eventId,
                    capacity_option_id: event.capacityId,
                },
                query: {
                    sync_pricing: syncPricing,
                    ...(superUser ? { superuser: superUser } : {}),
                },
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        options
    );
};

export const useCreateBooking = (
    options?: CustomMutationOptions<
        {
            customer: Customer;
            capacityId: string;
            productOptions: {
                id: string;
                customers: { items: any[]; pax: PaxData };
            }[];
            eventId?: string;
            isDraft?: boolean;
            pax: PaxData;
            notes: { value: string; type: NoteVisibility }[];
            source: {
                type: SourceType;
                name?: DirectSource;
                referral?: Referral;
            };
            time: Date;
            discount?: DiscountData;
            productId: string;
        },
        CreateBookingResDto | CreateBookingRes1Dto
    >
) => {
    const queryClient = useQueryClient();
    const superUser = useSuperUser();

    return useMutation({
        mutationFn: async params => {
            const {
                customer,
                eventId,
                isDraft,
                pax,
                productId,
                time,
                productOptions,
                notes,
                discount,
                capacityId,
                source,
            } = params;

            const response = await BookingsService.createBooking({
                body: {
                    source:
                        source.type === SourceType.DIRECT
                            ? {
                                  type: SourceType.DIRECT,
                                  name: source.name as DirectSource,
                              }
                            : {
                                  type: SourceType.REFERRAL,
                                  member_id: source.referral?.id as string,
                              },
                    product: {
                        id: productId,
                        date: {
                            timezone: {
                                name: Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone as TimezoneName,
                            },
                            start: convertDateToTimestamp(time, true),
                        },
                        capacity_option_id: capacityId,
                        customers: {
                            items: [
                                // @ts-ignore
                                {
                                    customer_id: customer.id as string,
                                },
                            ],
                            pax,
                        },
                        options: { items: productOptions },
                        pricing: discount
                            ? {
                                  discount,
                              }
                            : undefined,
                    },
                    event: eventId
                        ? {
                              id: eventId,
                          }
                        : undefined,
                    notes: notes?.length
                        ? {
                              items: notes.map(({ value, type }) => ({
                                  id: '',
                                  content: value,
                                  visibility: type,
                              })),
                          }
                        : undefined,
                    status: isDraft ? BookingStatus.DRAFT : BookingStatus.HOLD,
                },
                query: superUser ? { superuser: superUser } : {},
            });

            queryClient.invalidateQueries({
                queryKey: ['useOrdersLazyKey', {}],
                exact: true,
                refetchType: 'active',
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
    });
};

// TODO review this
export const useSetTravelerParticipation = (
    options?: CustomMutationOptions<
        {
            bookingId: string;
            travelerId: string;
            attendance: AttendanceType;
        },
        UpdateEventBookingsResDto | UpdateEventBookingsRes1Dto
    >
) => {
    const { update } = useBookingCache();

    return useMutation({
        mutationFn: async ({ bookingId, travelerId, attendance }) => {
            const response = await BookingsService.updateCustomersBookings({
                path: { booking_id: bookingId },
                body: {
                    items: [
                        {
                            id: travelerId,
                            attendance: { type: attendance },
                            action: ListItemAction.UPDATE,
                        },
                    ],
                },
            });

            if (!response.data) throw new Error('Error');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].bookingId, args[0]);
            options?.onSuccess?.(...args);
        },
        onError: (error, ...args) => {
            // @ts-ignore
            if (error?.status === 409) {
                options?.onSuccess?.({} as UpdateEventBookingsResDto, ...args);
            } else {
                options?.onError?.(error, ...args);
            }
        },
    });
};
